// M1 throwaway: FE-owned chat lifecycle. The agent and its session are
// provisioned eagerly when the sidepane opens so the first message doesn't pay
// provisioning latency; the resulting `(agentId, sessionId)` pair is persisted
// to sessionStorage per flow per tab and every send reuses it. Replace once the
// proper agent lifecycle lands.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSessionStorage } from "usehooks-ts";
import { v4 as uuidv4 } from "uuid";

import {
  AWAITING_SESSION_TIMEOUT_MS,
  copilotSessionKeys,
  TERMINAL_STATUSES,
  useCopilotInvoke,
  useCopilotMessages,
} from "src/api/agents/copilot";
import { agentManagementApi } from "src/api/endpoints";
import { useCurrentWorkspace, useFlow } from "src/api/queries";
import {
  type AgentVersionConfigurationInputToolsInner,
  AgentVersionConfigurationInputToolsInnerTypeEnum,
  Environment,
  Model,
  type SessionMessagesResponseMessagesInner,
  SessionStatus,
} from "src/clients/agent-management";
import { buildLiveAnalyticsSystemPrompt } from "src/flow/analytics/m1/liveAnalyticsSystemPrompt";
import {
  QUERY_METRICS_TOOL_DESCRIPTION,
  REPLY_VISUALIZE_METRICS_TOOL_DESCRIPTION,
} from "src/flow/analytics/m1/liveAnalyticsToolDescriptions";
import { SPECIAL_DOMAINS } from "src/flow/analytics/m1/VisualizeMetrics/liveAnalyticsDomains";
import { fetchPreflightBlocks } from "src/flow/analytics/m1/workspaceSchemaSnapshot";

const SLUG_PREFIX = "live-analytics-m1-";
const AGENT_NAME = "Live Analytics (M1)";
const AGENT_DESCRIPTION =
  "Auto-provisioned per-session Live Analytics chat agent (OPTI-6761 throwaway)";

// Keep the session resumable for 7 days; the backend default (24h) expires it
// too soon, so a chat can't be continued the next day.
const SESSION_MAX_LIFETIME_SECONDS = 7 * 24 * 60 * 60;
// Surface expiry on the FE a day before the backend hard limit so the user gets
// a graceful "start a new chat" message instead of a failed invoke at the edge.
const SESSION_EXPIRY_THRESHOLD_MS = 6 * 24 * 60 * 60 * 1000;
const SESSION_EXPIRED_MESSAGE =
  "This chat has expired. Start a new conversation to continue.";

const specialDomainsForAgent = (): Array<{
  name: string;
  description: string;
  values?: readonly string[];
}> =>
  Object.entries(SPECIAL_DOMAINS).map(([name, def]) => ({
    name,
    description: def.description,
    ...(def.values ? { values: def.values } : {}),
  }));

// sessionStorage (not localStorage) so a new tab starts a fresh conversation.
const SESSIONS_STORAGE_KEY = "liveAnalyticsM1Sessions";

type PersistedSession = { agentId: string; sessionId: string };
type FlowSessionMap = Record<string, PersistedSession>;

// `flow_slug` etc. are only enforced for FLOW tools; cast through `unknown`
// to satisfy the union TS type. Same pattern as `useCaseCopilotInvoke`.
const buildTools = (): AgentVersionConfigurationInputToolsInner[] =>
  [
    {
      id: uuidv4(),
      type: AgentVersionConfigurationInputToolsInnerTypeEnum.QUERY_METRICS,
      name: "query_metrics",
      description: QUERY_METRICS_TOOL_DESCRIPTION,
    },
    {
      id: uuidv4(),
      type: AgentVersionConfigurationInputToolsInnerTypeEnum.REPLY_VISUALIZE_METRICS,
      name: "reply_visualize_metrics",
      description: REPLY_VISUALIZE_METRICS_TOOL_DESCRIPTION,
      chart_format: "vega",
      special_domains: specialDomainsForAgent(),
    },
  ] as unknown as AgentVersionConfigurationInputToolsInner[];

type ProvisionParams = {
  baseUrl: string;
  flowId: string;
  flowName: string;
  flowVersionIdToName: Record<string, string>;
};

type TransientChatState =
  | { kind: "idle" }
  | { kind: "provisioning"; pendingMessage: string | null }
  | { kind: "error"; message: string };

const provisionAgent = async ({
  baseUrl,
  flowId,
  flowName,
  flowVersionIdToName,
}: ProvisionParams): Promise<string> => {
  const mgmt = agentManagementApi(baseUrl);

  const { workspaceSchemaBlock, flowVersionsBlock } =
    await fetchPreflightBlocks(baseUrl, flowId, flowVersionIdToName).catch(
      (err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        return {
          workspaceSchemaBlock: `_(schema snapshot unavailable — ${msg}; agent will discover keys on demand)_`,
          // Names still resolve on the chart via the label dictionary.
          flowVersionsBlock: "_(flow-version list unavailable)_",
        };
      },
    );

  const systemPrompt = buildLiveAnalyticsSystemPrompt({
    flowId,
    flowName,
    workspaceSchemaBlock,
    flowVersionsBlock,
  });

  const slug = SLUG_PREFIX + uuidv4().slice(0, 8);
  const createResp = await mgmt.createAgentCopilotsManagementApiV0AgentsPost({
    agentCreate: {
      slug,
      name: AGENT_NAME,
      description: AGENT_DESCRIPTION,
    },
  });
  const agentId = createResp.data.agent.id;

  const versionResp =
    await mgmt.createVersionCopilotsManagementApiV0AgentsAgentIdVersionsPost({
      agentId,
      agentVersionCreate: {
        name: "v1",
        description: "Auto-provisioned M1 Live Analytics version",
        configuration: {
          model_config: {
            model: Model.CLAUDE_4_5_SONNET,
            system_prompt: systemPrompt,
            temperature: 0,
          },
          tools: buildTools(),
          execution_config: {
            session_max_lifetime_seconds: SESSION_MAX_LIFETIME_SECONDS,
          },
        },
      },
    });
  const versionId = versionResp.data.agent_version.id;

  await mgmt.publishVersionCopilotsManagementApiV0AgentsAgentIdVersionsVersionIdPublishPost(
    {
      agentId,
      versionId,
      ifMatch: "0",
    },
  );

  return agentId;
};

export const useLiveAnalyticsChat = ({
  flowId,
  flowName,
}: {
  flowId: string;
  flowName: string;
}): {
  messages: SessionMessagesResponseMessagesInner[];
  isActive: boolean;
  isProvisioning: boolean;
  isReady: boolean;
  isExpired: boolean;
  failureMessage: string | null;
  send: (message: string) => void;
  resetSession: () => void;
} => {
  const { data: workspace } = useCurrentWorkspace();
  const baseUrl = workspace?.base_url;
  const queryClient = useQueryClient();

  // Same source the chart's UUID→name relabeling uses; archived versions are
  // included because recent decisions can reference them.
  const flowQuery = useFlow(flowId, { includeArchivedFlowVersions: true });
  // Names are truncated to bound prompt size; a workspace can have very long
  // version names. The row count is bounded separately by the metrics query's
  // LIMIT (which ranks by volume — capping this map instead would drop names
  // for high-volume versions, since API order isn't volume order).
  const flowVersionIdToName = useMemo(
    () =>
      Object.fromEntries(
        (flowQuery.data?.versions ?? []).map(
          (v) => [v.id, v.name.slice(0, 40)] as const,
        ),
      ),
    [flowQuery.data?.versions],
  );
  // Gate provisioning until the flow query settles so the baked prompt has
  // version names. Eager provisioning is one-shot (`eagerProvisionStartedRef`),
  // so starting before names arrive would strand an all-unnamed prompt until
  // the user resets the session. Errors still release the gate — names just
  // fall back to UUIDs.
  const flowVersionsResolved = flowQuery.isSuccess || flowQuery.isError;

  // Underscored — all writes go through `writeSession`.
  const [sessions, _setSessions] = useSessionStorage<FlowSessionMap>(
    SESSIONS_STORAGE_KEY,
    {},
  );

  const writeSession = useCallback(
    (id: string, session: PersistedSession | null) => {
      _setSessions((prev) => {
        if (session === null) {
          return Object.fromEntries(
            Object.entries(prev).filter(([k]) => k !== id),
          );
        }
        return { ...prev, [id]: session };
      });
    },
    [_setSessions],
  );

  const persistedSession = sessions[flowId] ?? null;

  const [transient, setTransient] = useState<TransientChatState>({
    kind: "idle",
  });

  const agentId = persistedSession?.agentId ?? null;
  const sessionId = persistedSession?.sessionId ?? null;

  const provisionMutation = useMutation({
    mutationFn: (params: ProvisionParams) => provisionAgent(params),
    onSuccess: (newAgentId, variables) => {
      writeSession(variables.flowId, {
        agentId: newAgentId,
        sessionId: uuidv4(),
      });
    },
    onError: (err: Error, variables) => {
      // Ignore a failure from a flow we've since navigated away from.
      if (variables.flowId !== flowId) return;
      setTransient({
        kind: "error",
        message: `Provision failed: ${err.message}`,
      });
    },
  });

  const awaitingSessionRef = useRef(false);
  const awaitingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  // Stops StrictMode's double-invoked effect from provisioning two agents.
  const eagerProvisionStartedRef = useRef(false);

  const messagesQueryKey = copilotSessionKeys.messages(sessionId);

  const invokeMutation = useCopilotInvoke({
    agentId: agentId ?? undefined,
    agentVersionId: "latest",
    sessionId,
    source: "api",
    environment: Environment.SANDBOX,
    messagesQueryKey,
    onInvokeStarted: () => {
      awaitingSessionRef.current = true;
      clearTimeout(awaitingTimeoutRef.current);
      awaitingTimeoutRef.current = setTimeout(() => {
        awaitingSessionRef.current = false;
      }, AWAITING_SESSION_TIMEOUT_MS);
    },
  });

  const clearTransientState = useCallback(() => {
    setTransient({ kind: "idle" });
    awaitingSessionRef.current = false;
    clearTimeout(awaitingTimeoutRef.current);
    eagerProvisionStartedRef.current = false;
    invokeMutation.reset();
    provisionMutation.reset();
  }, [invokeMutation.reset, provisionMutation.reset]);

  // Opening the pane is the only thing that provisions: create the agent +
  // session up front so they're ready before the first message. The
  // `flowVersionsResolved` flip is what releases this once flow metadata
  // loads; `eagerProvisionStartedRef` keeps it one-shot. We don't gate on
  // `transient.kind === "idle"` so a message buffered during the flow-load
  // wait (which moves transient to `provisioning`) still kicks off provision.
  useEffect(() => {
    if (!baseUrl || agentId !== null) return;
    if (!flowVersionsResolved) return;
    if (eagerProvisionStartedRef.current) return;
    eagerProvisionStartedRef.current = true;
    // Preserve a message the user sent while we waited on flow metadata.
    setTransient((prev) =>
      prev.kind === "provisioning"
        ? prev
        : { kind: "provisioning", pendingMessage: null },
    );
    provisionMutation
      .mutateAsync({ baseUrl, flowId, flowName, flowVersionIdToName })
      .catch(() => {
        // onError surfaces the failure
      });
  }, [
    baseUrl,
    agentId,
    flowId,
    flowName,
    flowVersionsResolved,
    flowVersionIdToName,
    provisionMutation.mutateAsync,
  ]);

  // In-flight provisions for the prior flow are left to complete — they
  // write to their captured `variables.flowId` slot. Known edge: the
  // buffered first message is lost.
  const [previousFlowId, setPreviousFlowId] = useState(flowId);
  if (previousFlowId !== flowId) {
    setPreviousFlowId(flowId);
    clearTransientState();
  }

  // Once provisioning settles, fire the buffered first message, if any.
  useEffect(() => {
    if (
      transient.kind !== "provisioning" ||
      agentId === null ||
      sessionId === null
    )
      return;
    const { pendingMessage } = transient;
    setTransient({ kind: "idle" });
    if (pendingMessage !== null) {
      invokeMutation.mutateAsync(pendingMessage).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit invokeMutation; run only when the provision settles
  }, [transient, agentId, sessionId]);

  const messagesQuery = useCopilotMessages(sessionId, {
    awaitingSessionRef,
    isPending: invokeMutation.isPending,
  });

  const rawMessages: SessionMessagesResponseMessagesInner[] = (
    messagesQuery.data?.pages.flatMap((p) => p.messages ?? []) ?? []
  ).toReversed();

  const sessionStatus = messagesQuery.data?.pages[0]?.session?.status;
  const isSessionTerminal =
    sessionStatus != null && TERMINAL_STATUSES.has(sessionStatus);

  const sessionCreatedAt = messagesQuery.data?.pages[0]?.session?.created_at;
  const isSessionExpired =
    sessionCreatedAt != null &&
    Date.now() - new Date(sessionCreatedAt).getTime() >
      SESSION_EXPIRY_THRESHOLD_MS;

  const send = useCallback(
    async (message: string) => {
      const trimmed = message.trim();
      if (!trimmed || !baseUrl || isSessionExpired) return;

      if (agentId !== null && sessionId !== null && transient.kind === "idle") {
        try {
          await invokeMutation.mutateAsync(trimmed);
        } catch {
          // onError handles UI
        }
        return;
      }

      // Don't pile onto a failed provision.
      if (transient.kind === "error") return;

      // Not ready yet — either provisioning is in flight, or we're still
      // waiting on flow metadata before it starts. Buffer the message; the
      // settle effect fires it once the agent + session exist.
      setTransient({ kind: "provisioning", pendingMessage: trimmed });
    },
    [
      baseUrl,
      agentId,
      sessionId,
      transient,
      invokeMutation.mutateAsync,
      isSessionExpired,
    ],
  );

  const resetSession = useCallback(() => {
    // Refuse to clear out from under an in-flight provision.
    if (transient.kind === "provisioning") return;
    clearTransientState();
    writeSession(flowId, null);
    queryClient.removeQueries({ queryKey: messagesQueryKey });
  }, [
    clearTransientState,
    transient,
    writeSession,
    flowId,
    queryClient,
    messagesQueryKey,
  ]);

  const isAwaitingFirstPoll =
    awaitingSessionRef.current && sessionStatus == null;

  // A user is waiting once a message is buffered onto an in-flight provision.
  // A bare eager provision must not make the UI look busy or hide the empty
  // state.
  const hasPendingMessage =
    transient.kind === "provisioning" && transient.pendingMessage !== null;

  const isActive =
    hasPendingMessage ||
    invokeMutation.isPending ||
    isAwaitingFirstPoll ||
    (sessionStatus != null && !isSessionTerminal);

  const invokeErrorMessage = invokeMutation.isError
    ? invokeMutation.error.message
    : null;

  const sessionFailedMessage =
    sessionStatus === SessionStatus.FAILED
      ? messagesQuery.data?.pages[0]?.session?.termination_reason
        ? `Something went wrong: ${messagesQuery.data.pages[0].session.termination_reason}`
        : "Something went wrong"
      : null;

  const failureMessage =
    (transient.kind === "error" ? transient.message : null) ??
    (isSessionExpired ? SESSION_EXPIRED_MESSAGE : null) ??
    invokeErrorMessage ??
    sessionFailedMessage;

  return {
    messages: rawMessages,
    isActive,
    isProvisioning: provisionMutation.isPending && hasPendingMessage,
    isReady: baseUrl != null,
    isExpired: isSessionExpired,
    failureMessage,
    send,
    resetSession,
  };
};
